import { getDomainLevel } from './url.js';
import { getCookie, removeCookie } from './cookie.js';
import { SID } from './constants.js';
/**
 * Accepts sid candidate and returns true if sid is valid and false if not.
 * It can handle any value, only sid in string format may evaluate to true.
 * @param sid Potentially valid sid
 * @return True if valid sid, false otherwise
 */
export const isValidSid = (sid) => typeof sid === 'string' && !!sid.match(/^id=(\d+)\|t=(\d+\.\d{3})\|te=(\d+\.\d{3})\|c=([A-Z0-9]{32})$/);
/**
 * Encodes sid into "znstring". Can handle any input, but only
 * valid sid in string format will yield actual hash.
 * @param sid Potentially valid sid
 * @return Hash if valid sid, null otherwise
 */
export const sidToHash = (sid) => {
    if (!isValidSid(sid)) {
        return null;
    }
    // atob and btoa may throw an exception, see https://developer.mozilla.org/en-US/docs/Web/API/atob#exceptions
    try {
        return window.btoa(encodeURIComponent(sid));
    }
    catch {
        return null;
    }
};
/**
 * Decodes "znstring" into sid. Can handle any input, but only
 * valid "znstring" in string format will yield sid.
 * @param hash Potentially hashed sid
 * @return Sid if valid hash, null otherwise
 */
export const hashToSid = (hash) => {
    if (typeof hash !== 'string') {
        return null;
    }
    // atob and btoa may throw an exception, see https://developer.mozilla.org/en-US/docs/Web/API/atob#exceptions
    try {
        const sid = decodeURIComponent(window.atob(hash));
        if (isValidSid(sid)) {
            return sid;
        }
        else {
            return null;
        }
    }
    catch {
        return null;
    }
};
/**
 * Retrieves sid cookie, validates its value and returns valid sid or null.
 * @return Valid sid or null
 */
export const getValidSidFromCookie = () => {
    const sidCookieValue = getCookie(SID);
    return isValidSid(sidCookieValue) ? sidCookieValue : null;
};
/**
 * Retrieves sid cookie, validates its value and removes the cookie
 * if the value is not a valid sid.
 */
export const removeInvalidSidCookie = () => {
    if (!isValidSid(getCookie(SID))) {
        removeCookie(SID, { domain: getDomainLevel(), path: '/' });
    }
};
